import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IAsignacion,
  IFilter,
  IPopulate,
  IQueryParam,
  IUsuario,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import {
  IColumnas,
  IRowLink,
} from '../../../../auxiliares/tabla/tabla.component';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { ActivatedRoute } from '@angular/router';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { estadoHtml, fechaCelda } from '../../../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { rutaDetalleEntidad } from '../../../../auxiliares/servicios/rutas-entidades';

@Component({
  standalone: true,
  imports: [AuxiliaresModule],
  selector: 'app-listado-asignacions',
  templateUrl: './listado-asignacions.component.html',
  styleUrl: './listado-asignacions.component.scss',
})
export class ListadoAsignacionsComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private route = inject(ActivatedRoute);

  public name = 'ListadoAsignacionsComponent';
  public asignacions?: IAsignacion[];
  public totalCount = 0;
  public columnas?: IColumnas<IAsignacion>[];
  public asignacions$?: Subscription;

  // Parámetros: pueden venir por @Input() o por ruta
  @Input() idEntidad?: string;
  @Input() tipoEntidad: 'modificada' | 'asignada' = 'modificada';
  public search: IRegExpSearch = {
    fields: [
      'nombreEntidadModificada',
      'nombreEntidadAsignada',
      'tipoEntidadModificada',
      'tipoEntidadAsignada',
    ],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];
  //Query
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'usuario',
      select: 'usuario datosPersonales.nombre',
    },
    {
      path: 'choferModificado',
      select: 'usuario datosPersonales.nombre',
    },
    {
      path: 'activoModificado',
      select: 'nombre identificacion categoria',
    },
    {
      path: 'trackerModificado',
      select: 'nombre deveui',
    },
    {
      path: 'alarmaModificada',
      select: 'nombre',
    },
    {
      path: 'choferAsignado',
      select: 'usuario datosPersonales.nombre',
    },
    {
      path: 'activoAsignado',
      select: 'nombre identificacion categoria',
    },
    {
      path: 'trackerAsignado',
      select: 'nombre deveui',
    },
    {
      path: 'simAsignado',
      select: 'iccid numero',
    },
  ];
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: '-fechaAsignacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  public async listar(): Promise<void> {
    // Construir filtro basado en los parámetros de ruta
    if (this.idEntidad) {
      const filter: IFilter<IAsignacion> = this.query.filter
        ? JSON.parse(this.query.filter)
        : {};

      if (this.tipoEntidad === 'modificada') {
        filter.idEntidadModificada = this.idEntidad;
      } else {
        filter.idEntidadAsignada = this.idEntidad;
      }

      this.query.filter = JSON.stringify(filter);
    }

    this.asignacions$?.unsubscribe();
    this.asignacions$ = this.lista2
      .asignacions(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.asignacions = data.datos;
      });
  }

  /// Celdas

  /**
   * Nombre visible de un lado de la asignación. Prioriza el nombre guardado
   * en la asignación (queda fijo aunque la entidad cambie de nombre) y, si no
   * hay, lo arma con lo populado.
   */
  private nombreEntidad(dato: IAsignacion, lado: 'modificada' | 'asignada') {
    if (lado === 'modificada') {
      if (dato.nombreEntidadModificada) return dato.nombreEntidadModificada;
      const chofer = dato.choferModificado;
      if (chofer) return this.nombreUsuario(chofer);
      return (
        dato.activoModificado?.identificacion ||
        dato.trackerModificado?.nombre ||
        dato.alarmaModificada?.nombre
      );
    }
    if (dato.nombreEntidadAsignada) return dato.nombreEntidadAsignada;
    const chofer = dato.choferAsignado;
    if (chofer) return this.nombreUsuario(chofer);
    return (
      dato.activoAsignado?.identificacion ||
      dato.trackerAsignado?.nombre ||
      dato.simAsignado?.iccid ||
      dato.simAsignado?.numero
    );
  }

  /**
   * Link al detalle de la entidad de un lado de la asignación. Si el tipo no
   * tiene detalle conocido, el nombre se muestra como texto plano.
   */
  private linkEntidad(
    dato: IAsignacion,
    lado: 'modificada' | 'asignada',
  ): IRowLink[] {
    const nombre = this.nombreEntidad(dato, lado)?.trim() || 'Sin nombre';
    const tipo =
      lado === 'modificada'
        ? dato.tipoEntidadModificada
        : dato.tipoEntidadAsignada;
    const id =
      lado === 'modificada' ? dato.idEntidadModificada : dato.idEntidadAsignada;
    const categoria =
      lado === 'modificada'
        ? dato.activoModificado?.categoria
        : dato.activoAsignado?.categoria;

    const ruta = rutaDetalleEntidad(tipo, id, categoria);
    if (!ruta) return [{ link: nombre }];
    return [{ link: nombre, route: ruta }];
  }

  /** Usuario que hizo la asignación; sin usuario la hizo el sistema. */
  private responsable(dato: IAsignacion): string {
    if (!dato.idUsuario) return 'Sistema';
    return this.nombreUsuario(dato.usuario) || 'Usuario sin nombre';
  }

  private nombreUsuario(usuario?: IUsuario): string | undefined {
    return usuario?.datosPersonales?.nombre || usuario?.usuario;
  }

  private setColumnas() {
    // En cada modo un lado de la asignación es siempre la misma entidad (la
    // del detalle donde está embebido el listado): esas columnas no aportan.
    const mostrarModificada =
      !this.idEntidad || this.tipoEntidad !== 'modificada';
    const mostrarAsignada = !this.idEntidad || this.tipoEntidad !== 'asignada';

    const columnas: IColumnas<IAsignacion>[] = [];

    if (mostrarModificada) {
      columnas.push(
        // Entidad Modificada
        {
          header: {
            label: 'Entidad modificada',
            sort: 'nombreEntidadModificada',
            sortable: true,
          },
          row: {
            links: (dato) => this.linkEntidad(dato, 'modificada'),
          },
        },
        // Tipo Entidad Modificada
        {
          header: {
            label: 'Tipo',
            sortable: true,
          },
          row: {
            field: 'tipoEntidadModificada',
          },
        },
      );
    }

    if (mostrarAsignada) {
      columnas.push(
        // Entidad Asignada
        {
          header: {
            label: 'Entidad asignada',
            sort: 'nombreEntidadAsignada',
            sortable: true,
          },
          row: {
            links: (dato) => this.linkEntidad(dato, 'asignada'),
          },
        },
        // Tipo Entidad Asignada
        {
          header: {
            label: 'Tipo',
            sortable: true,
          },
          row: {
            field: 'tipoEntidadAsignada',
          },
        },
      );
    }

    columnas.push(
      // Fecha asignación
      {
        header: {
          label: 'Fecha asignación',
          sort: 'fechaAsignacion',
          sortable: true,
        },
        row: {
          fecha: (dato) => dato.fechaAsignacion,
          noData: 'Sin fecha',
        },
      },
      // Fecha desasignación: la fecha si la hay; si no, la asignación sigue
      // vigente y se muestra como estado.
      {
        header: {
          label: 'Fecha desasignación',
          sort: 'fechaDesasignacion',
          sortable: true,
        },
        row: {
          html: (dato) =>
            dato.fechaDesasignacion
              ? `<span class="tabla-fecha">${escaparHtml(fechaCelda(dato.fechaDesasignacion))}</span>`
              : estadoHtml({
                  texto: 'Vigente',
                  tono: 'ok',
                  icono: 'toggle_on',
                }),
        },
      },
      // Responsable
      {
        header: {
          label: 'Responsable',
        },
        row: {
          parse: (dato) => this.responsable(dato),
          tooltip: (dato) =>
            dato.idUsuario
              ? undefined
              : 'Asignación automática, sin intervención de un usuario',
        },
      },
    );

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    this.columnas = columnas;
  }

  public async ngOnInit(): Promise<void> {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }

    // Si los parámetros vienen por @Input(), usar esos
    if (this.idEntidad) {
      this.setColumnas();
      await this.listar();
    } else {
      // Si no, intentar leer de la ruta
      this.route.params.subscribe(async (params) => {
        this.idEntidad = params['id'];
        this.tipoEntidad =
          params['tipo'] === 'asignada' ? 'asignada' : 'modificada';

        this.setColumnas();
        await this.listar();
      });
    }
  }

  public async ngOnDestroy(): Promise<void> {
    this.asignacions$?.unsubscribe();
  }
}
